/**
 * Positioning Panel
 * Cumulative funding, top trader sentiment, net position flow, futures basis
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassPositioning } from '@/hooks/use-coinglass';
import { formatPercent } from '@/lib/utils';
import type { Asset } from '@/stores/alert-store';

interface PositioningPanelProps {
  asset: Asset;
}

export function PositioningPanel({ asset }: PositioningPanelProps) {
  const { data, isLoading } = useCoinglassPositioning(asset);

  if (isLoading) return <LoadingSkeleton className="h-64" lines={5} />;

  if (!data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Positioning</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No data</p>
        </CardContent>
      </Card>
    );
  }

  const { cumulativeFunding, topTraderSentiment, netPositionFlow, futuresBasis } = data;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Positioning</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cumulative Funding */}
        {cumulativeFunding && cumulativeFunding.day1 != null && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Cumulative Funding</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">1D: </span>
                <span className={cumulativeFunding.day1 > 0 ? 'text-red-500' : 'text-green-500'}>
                  {formatPercent(cumulativeFunding.day1 * 100, 4)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">7D: </span>
                <span className={(cumulativeFunding.day7 ?? 0) > 0 ? 'text-red-500' : 'text-green-500'}>
                  {formatPercent((cumulativeFunding.day7 ?? 0) * 100, 3)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">30D: </span>
                <span className={(cumulativeFunding.day30 ?? 0) > 0 ? 'text-red-500' : 'text-green-500'}>
                  {formatPercent((cumulativeFunding.day30 ?? 0) * 100, 2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Top Trader Sentiment */}
        {topTraderSentiment && topTraderSentiment.longRatio != null && topTraderSentiment.shortRatio != null && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Top Traders</p>
            <div className="flex items-center gap-2">
              <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="bg-green-500 transition-all"
                  style={{ width: `${topTraderSentiment.longRatio * 100}%` }}
                />
                <div
                  className="bg-red-500 transition-all"
                  style={{ width: `${topTraderSentiment.shortRatio * 100}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {(topTraderSentiment.longRatio * 100).toFixed(0)}L / {(topTraderSentiment.shortRatio * 100).toFixed(0)}S
              </span>
            </div>
          </div>
        )}

        {/* Net Position Flow */}
        {netPositionFlow && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Net Position Flow</p>
            <div className="flex gap-4 text-xs">
              <span className="text-green-500">
                Long: {formatPercent(netPositionFlow.netLongChange, 2)}
              </span>
              <span className="text-red-500">
                Short: {formatPercent(netPositionFlow.netShortChange, 2)}
              </span>
            </div>
          </div>
        )}

        {/* Futures Basis */}
        {futuresBasis && futuresBasis.annualizedRate != null && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Futures Basis</p>
            <div className="flex gap-4 text-xs">
              <span>
                Annualized: <span className={futuresBasis.annualizedRate > 0 ? 'text-green-500' : 'text-red-500'}>
                  {formatPercent(futuresBasis.annualizedRate * 100, 2)}
                </span>
              </span>
              <span>
                Spread: {formatPercent((futuresBasis.spread ?? 0) * 100, 4)}
              </span>
            </div>
          </div>
        )}

        {!cumulativeFunding && !topTraderSentiment && !netPositionFlow && !futuresBasis && (
          <p className="text-sm text-muted-foreground">No positioning data</p>
        )}
      </CardContent>
    </Card>
  );
}
